import { useContext } from 'react';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

import { BeamlineDto} from '../api/api.ts'
import { MetadataContext } from '../metadataProvider.tsx'
import { InputAutocomplete, SearchFunctions, SearchResult, SelectingStatus } from './inputAutocomplete.tsx'
import { highlightSearchTermsInString } from "../utils.tsx";
import './beamlineAutocomplete.css';


interface BeamlineSearchFunctions {
  itemSelected: (item: BeamlineDto) => SelectingStatus;
  selectedNone: () => SelectingStatus;
}


// Settings passed in with the React component
interface BeamlineAutocompleteParameters {
  allowedBeamlineSlugs?: string[] | null;
  selectedItem: BeamlineDto | null;
  searchFunctions: BeamlineSearchFunctions;
  isReadOnly?: boolean;
  greenWhenValid?: boolean;
  canSelectNone?: boolean;
  showClearButton?: boolean;
  triggerFocus?: boolean;
  resetOnDefocus?: boolean;
  inputSize?: string;
  iconSize?: SizeProp;
  placeholder?: string;
}


function BeamlineAutocomplete(settings:BeamlineAutocompleteParameters) {

  const metadataContext = useContext(MetadataContext);

  async function searchBeamlines(searchString: string): Promise<SearchResult<BeamlineDto>> {
    const searchStringLower = searchString.toLowerCase();
    // If allowedBeamlineSlugs is an array, we use it even if it's empty.
    const idsToTest = Array.isArray(settings.allowedBeamlineSlugs) ? settings.allowedBeamlineSlugs : metadataContext.beamlines.slugsInDisplayOrder;
    const toTest = idsToTest.map((t) => metadataContext.beamlines.bySlug.get(t)!);
    const matches = toTest.filter((s) =>
                      s.name.toLowerCase().includes(searchStringLower) ||
                      (s.description && s.description.toLowerCase().includes(searchStringLower)));
    return { matches: matches };
  };

  function renderMatch(item: BeamlineDto, searchString: string): React.JSX.Element {
    return (<>
              <td>{ highlightSearchTermsInString(item.name, [searchString]) }</td>
              <td>{ highlightSearchTermsInString(item.description || "", [searchString]) }</td>
            </>);
  }

  const beamlineSearchFunctions: SearchFunctions<BeamlineDto> = {
    itemToString: (item) => { return item.name },
    itemsRetriever: searchBeamlines,
    itemSelected: settings.searchFunctions.itemSelected,
    selectedNone: settings.searchFunctions.selectedNone,
    renderMatchedItem: renderMatch
  };

  return InputAutocomplete<BeamlineDto>({
      value: settings.selectedItem ? settings.selectedItem.name : "",
      selectedItem: settings.selectedItem,
      searchFunctions: beamlineSearchFunctions,
      addedClass: "beamline",
      inputSize: settings.inputSize,
      iconSize: settings.iconSize,
      placeholder: settings.placeholder || "Select a Beamline",
      isReadOnly: settings.isReadOnly,
      greenWhenValid: settings.greenWhenValid,
      canSelectNone: settings.canSelectNone,
      showClearButton: settings.showClearButton,
      resetOnDefocus: settings.resetOnDefocus,
      triggerFocus: settings.triggerFocus,
      showAllByDefault: true,
      renderResultsAsTable: true
    });
}

export { BeamlineAutocomplete }
export type { BeamlineSearchFunctions }